/**
 * SYSTEMATIC FIX — pin ALL date/time locale formatting to the venue timezone (IST).
 *
 * LA Mirage is a single-venue business; every wedding, event, booking, shift and
 * payment happens in Asia/Kolkata. But `new Date(x).toLocaleDateString()` (and the
 * time/`toLocaleString` variants) with NO `timeZone` option render in the VIEWER's
 * BROWSER timezone. For a viewer WEST of IST (e.g. the Americas), a wedding date
 * stored at midnight (UTC midnight, or IST-midnight = 18:30Z the day before) crosses
 * the day boundary and shows the PREVIOUS DAY — e.g. a 12-Sep wedding displays as
 * 11-Sep. This was reported repeatedly (see memory: client date display browser-tz).
 *
 * Rather than hunt down 240+ call sites (and re-hunt every new one), we set the
 * DEFAULT timezone for Date's locale-formatting methods to IST, ONCE, at app start.
 *
 * Guarantees:
 *  - Only `Date.prototype` methods are patched — `Number.prototype.toLocaleString`
 *    (currency like ₹1,23,456) is UNTOUCHED.
 *  - Any call that PASSES an explicit `timeZone` is left exactly as-is (the existing
 *    IST-pinned helpers in dateUtils.ts, and any deliberate UTC/other-tz formatting).
 *  - Idempotent — safe under Vite HMR (guarded by a symbol flag).
 *
 * If you ever genuinely need a viewer-local time, pass `{ timeZone: undefined }`
 * explicitly won't work (we treat null/undefined as "use IST"); instead read the
 * raw Date components. That is virtually never wanted in this app.
 */

const IST_TIMEZONE = 'Asia/Kolkata';
const PATCHED = Symbol.for('lamirage.istDateDefault.patched');

type LocaleMethod = 'toLocaleDateString' | 'toLocaleString' | 'toLocaleTimeString';

export function installIstDateDefault(): void {
  const proto = Date.prototype as any;
  if (proto[PATCHED]) return; // already installed (HMR / double-import)

  const methods: LocaleMethod[] = ['toLocaleDateString', 'toLocaleString', 'toLocaleTimeString'];
  for (const method of methods) {
    const original = proto[method] as (
      locales?: Intl.LocalesArgument,
      options?: Intl.DateTimeFormatOptions,
    ) => string;

    proto[method] = function patched(
      this: Date,
      locales?: Intl.LocalesArgument,
      options?: Intl.DateTimeFormatOptions,
    ): string {
      // Only inject a default when the caller did NOT specify a timeZone.
      if (!options || options.timeZone == null) {
        options = { ...(options ?? {}), timeZone: IST_TIMEZONE };
      }
      return original.call(this, locales, options);
    };
  }

  // ── The hole this patch used to leave open ────────────────────────────────
  // Patching Date.prototype only covers `date.toLocaleString(...)`. Code that
  // reaches for `new Intl.DateTimeFormat(undefined, {...}).format(date)` —
  // which is the idiomatic way to format a date ONCE and reuse the formatter —
  // bypassed all of the above and rendered in the VIEWER's timezone.
  //
  // Reported 2026-08-19: the live watch page showed a 6:00 PM IST wedding as
  // "04:00 AM" to a viewer in the UAE (UTC+4), from
  // `new Intl.DateTimeFormat(undefined, { hour: '2-digit', ... })` in
  // LiveWatch.tsx. Five such call sites existed across four files.
  //
  // Rather than fix five sites and re-hunt every new one, default the
  // CONSTRUCTOR too. Same contract as above: an explicit `timeZone` always
  // wins, so deliberate UTC/other-tz formatting is untouched.
  const OriginalDTF = Intl.DateTimeFormat;
  const PatchedDTF = function (
    this: unknown,
    locales?: Intl.LocalesArgument,
    options?: Intl.DateTimeFormatOptions,
  ) {
    const opts: Intl.DateTimeFormatOptions =
      !options || options.timeZone == null
        ? { ...(options ?? {}), timeZone: IST_TIMEZONE }
        : options;
    // Always construct via the original. A constructor that returns an object
    // overrides `this`, so this works for both `new Intl.DateTimeFormat(...)`
    // and a bare `Intl.DateTimeFormat(...)` call.
    return new (OriginalDTF as any)(locales, opts);
  } as unknown as typeof Intl.DateTimeFormat;

  // `prototype` is read-only on a function type, so define it rather than assign.
  // Keeping the ORIGINAL prototype means `x instanceof Intl.DateTimeFormat` and
  // every formatter method still behave exactly as before.
  Object.defineProperty(PatchedDTF, 'prototype', {
    value: OriginalDTF.prototype,
    writable: false,
    enumerable: false,
    configurable: false,
  });
  (PatchedDTF as any).supportedLocalesOf = OriginalDTF.supportedLocalesOf.bind(OriginalDTF);
  Intl.DateTimeFormat = PatchedDTF;

  Object.defineProperty(proto, PATCHED, { value: true, enumerable: false });
}
